import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { DeviceMobileIcon } from '@phosphor-icons/react/DeviceMobile';
import { LaptopIcon } from '@phosphor-icons/react/Laptop';
import { TelevisionIcon } from '@phosphor-icons/react/Television';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Section, TargetingElementKeys, type TargetingElementKeysType } from '../../context/types.ts';
import { convertApiToSet } from '../../utils/convertApiToSet.ts';

export const useReadOnlyDeviceInitialState = (
  targetingParams: RequiredTargetingFormType | null | undefined
): Section => {
  const { t } = useTranslation();

  return useMemo(() => {
    const formValue = targetingParams?.device_type;
    const initValues = convertApiToSet([formValue?.desktop_devices, formValue?.mobile_devices, formValue?.tv_devices]);
    const isSelected = (key: TargetingElementKeysType): boolean => initValues.has(key);

    return {
      label: t('Device'),
      isLoading: true,
      elements: [
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_DESKTOP,
            label: t('Desktop'),
            icon: <LaptopIcon size={24} />,
            isSelected: isSelected(TargetingElementKeys.DEVICE_DESKTOP),
            isDisabled: true,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_MOBILE,
            label: t('Mobile'),
            icon: <DeviceMobileIcon size={18} />,
            isSelected:
              isSelected(TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE) ||
              isSelected(TargetingElementKeys.DEVICE_MOBILE_TABLET),
            isDisabled: true,
          },
          subOptions: [
            {
              key: TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE,
              label: t('Smart Phone'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_MOBILE_SMARTPHONE),
              isDisabled: true,
            },
            {
              key: TargetingElementKeys.DEVICE_MOBILE_TABLET,
              label: t('Tablet'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_MOBILE_TABLET),
              isDisabled: true,
            },
          ],
        },
        {
          mainOption: {
            key: TargetingElementKeys.DEVICE_TV,
            label: t('TV Devices'),
            icon: <TelevisionIcon size={18} />,
            isSelected:
              isSelected(TargetingElementKeys.DEVICE_TV_CONNECTEDTV) ||
              isSelected(TargetingElementKeys.DEVICE_TV_SETTOPBOX),
            isDisabled: true,
          },
          subOptions: [
            {
              key: TargetingElementKeys.DEVICE_TV_CONNECTEDTV,
              label: t('Connected TV'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_TV_CONNECTEDTV),
              isDisabled: true,
            },
            {
              key: TargetingElementKeys.DEVICE_TV_SETTOPBOX,
              label: t('Set Top Box'),
              isSelected: isSelected(TargetingElementKeys.DEVICE_TV_SETTOPBOX),
              isDisabled: true,
            },
          ],
        },
      ],
    };
  }, [t, targetingParams]);
};
